/**
 * RuntimeParameters — Phase 21 entry. PLATFORM_ADMIN console that
 * lists every org + user across the platform and lets the operator
 * "Connect to user runtime" to drill into that user's OpenClaw settings
 * via the /api/lx/openclaw/* surface (proxied cross-user with
 * X-Target-User-Id; see lexserver runtime-proxy + the typed client at
 * `lexportal/src/api/lx-openclaw-config.ts`).
 *
 * Two views, URL-driven for shareable deep-links + browser back/forward:
 *   1. /runtime-parameters          → org list (paginated)
 *      /runtime-parameters?orgId=…  → users-in-org with runtime status
 *   2. /runtime-parameters/:userId  → per-user UserRuntimePane (8 tabs)
 *                                    (UserRuntimePane lands in slice 4)
 *
 * Handoff §10 hard rule: do NOT cache user/org settings in localStorage.
 * The per-user pane re-fetches on focus; this list view only paginates
 * the org metadata which is non-sensitive + already cached server-side.
 *
 * Performance: settings are NOT eagerly fetched per user in the list
 * view (handoff §7). Per-user fetch happens once the operator picks a
 * row and lands on the detail sub-route.
 *
 * Reference: ai-docs/handoffs/lexportal-openclaw-settings-handoff.md.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  ChevronRight,
  Loader2,
  MonitorCog,
  User as UserIcon,
} from "lucide-react";
import { usePlatformApi } from "@/hooks/usePlatformApi";
import { useRuntimeVersions } from "@/hooks/useRuntimeVersions";
import { PlatformApiError } from "@/lib/platform-api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Organization, OrgUser } from "@/types/platform";

function errMsg(e: unknown, fallback: string): string {
  return e instanceof PlatformApiError
    ? `${e.code}: ${e.message}`
    : e instanceof Error
      ? e.message
      : fallback;
}

export function RuntimeParameters() {
  // The /:userId detail sub-route is owned by `UserRuntimePaneRoute`
  // (see App.tsx). This component only renders the org/users list view.
  return <RuntimeParametersListView />;
}

function RuntimeParametersListView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedOrgId = searchParams.get("orgId");

  const setSelectedOrg = useCallback(
    (orgId: string | null) => {
      setSearchParams(orgId ? { orgId } : {}, { replace: false });
    },
    [setSearchParams],
  );

  if (selectedOrgId) {
    return (
      <OrgUsersWithRuntimeStatus
        orgId={selectedOrgId}
        onBack={() => setSelectedOrg(null)}
      />
    );
  }
  return <OrgsListView onPickOrg={setSelectedOrg} />;
}

// ─── Org list ─────────────────────────────────────────────────────────

function OrgsListView({ onPickOrg }: { onPickOrg: (orgId: string) => void }) {
  const api = usePlatformApi();
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Active orgs only — Deleted/Terminated orgs have no live
        // runtimes to manage. Operators who specifically need the
        // recycle bin already have a dedicated entry point.
        const res = await api.listOrgs({ status: "ACTIVE", limit: 200 });
        if (cancelled) return;
        setOrgs(res.data);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(errMsg(e, "Failed to load organizations"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  return (
    <div
      className="flex flex-col gap-4 p-6"
      data-testid="runtime-parameters-page"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <MonitorCog className="h-6 w-6 text-primary" />
            Runtime Parameters
          </h1>
          <p className="text-sm text-muted-foreground">
            Cross-user PLATFORM_ADMIN console for the per-user lexruntime
            OpenClaw settings surface. Pick an organization → select a user →
            view + tune that user's runtime parameters. Audit rows land in
            the operational-logs pipeline under{" "}
            <code className="font-mono text-foreground">subsystem=lx-openclaw-*</code>.
          </p>
        </div>
      </div>

      {error && (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          data-testid="runtime-parameters-error"
        >
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Organization</TableHead>
              <TableHead className="w-32">Slug</TableHead>
              <TableHead className="w-24">Status</TableHead>
              <TableHead className="w-8" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Loader2 className="h-4 w-4 animate-spin" />
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              orgs.map((org) => (
                <TableRow
                  key={org.orgId}
                  className="cursor-pointer hover:bg-accent/40"
                  onClick={() => onPickOrg(org.orgId)}
                  data-testid={`runtime-parameters-org-row-${org.orgId}`}
                >
                  <TableCell>
                    <span className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      {org.name}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-xs text-muted-foreground">
                      {org.slug}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="default">{org.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    <ChevronRight className="h-4 w-4" />
                  </TableCell>
                </TableRow>
              ))}
            {!loading && orgs.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-sm text-muted-foreground">
                  No active organizations.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ─── Users-in-org view ────────────────────────────────────────────────

function OrgUsersWithRuntimeStatus({
  orgId,
  onBack,
}: {
  orgId: string;
  onBack: () => void;
}) {
  const api = usePlatformApi();
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [orgName, setOrgName] = useState<string>(orgId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reuse the existing batch endpoint that powers /runtimes — it returns
  // {userId → version+skew} for the whole org, so we get the status
  // chip without N round-trips. When the endpoint isn't live the hook's
  // `available === false` short-circuits us into "status unavailable".
  const { byUserId, available, error: versionsError } = useRuntimeVersions(orgId);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [orgRes, usersRes] = await Promise.all([
          api.getOrg(orgId),
          api.listOrgUsers(orgId, { limit: 200 }),
        ]);
        if (cancelled) return;
        setOrgName(orgRes.data.name);
        setUsers(usersRes.data);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(errMsg(e, "Failed to load users"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, orgId]);

  const activeUsers = useMemo(
    () => users.filter((u) => u.status === "ACTIVE"),
    [users],
  );

  return (
    <div
      className="flex flex-col gap-4 p-6"
      data-testid="runtime-parameters-org-users"
    >
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onBack}
          data-testid="runtime-parameters-back-btn"
        >
          <ArrowLeft className="mr-1 h-4 w-4" /> Back
        </Button>
        <h2 className="flex items-center gap-2 text-lg">
          <Building2 className="h-5 w-5 text-muted-foreground" />
          {orgName}
        </h2>
        <span className="text-xs text-muted-foreground">
          ({activeUsers.length} active users)
        </span>
      </div>

      {error && (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          data-testid="runtime-parameters-error"
        >
          {error}
        </div>
      )}

      {versionsError && available && (
        <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
          Runtime version probe failed: {versionsError}. Status column may be
          incomplete.
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead className="w-48">Email</TableHead>
              <TableHead className="w-24">Role</TableHead>
              <TableHead className="w-40">Runtime status</TableHead>
              <TableHead className="w-40">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Loader2 className="h-4 w-4 animate-spin" />
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              activeUsers.map((user) => {
                const version = byUserId.get(user.userId);
                const status = !available
                  ? "unknown"
                  : version
                    ? version.skew ?? "unknown"
                    : "no runtime";
                const statusVariant: Parameters<typeof Badge>[0]["variant"] =
                  status === "current"
                    ? "default"
                    : status === "unreachable" || status === "no runtime"
                      ? "destructive"
                      : "outline";
                return (
                  <TableRow
                    key={user.userId}
                    data-testid={`runtime-parameters-user-row-${user.userId}`}
                  >
                    <TableCell>
                      <span className="flex items-center gap-2">
                        <UserIcon className="h-4 w-4 text-muted-foreground" />
                        {user.displayName || `${user.firstName} ${user.lastName}`}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {user.email}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{user.role}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariant}>{status}</Badge>
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/runtime-parameters/${user.userId}`}
                        data-testid={`runtime-parameters-connect-${user.userId}`}
                      >
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={status === "no runtime"}
                          title={
                            status === "no runtime"
                              ? "User has no provisioned runtime"
                              : "Open the per-user runtime parameters pane"
                          }
                        >
                          Connect to runtime
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })}
            {!loading && activeUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-sm text-muted-foreground">
                  No active users in this organization.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// (The per-user pane lives in `UserRuntimePane.tsx` and is mounted by
//  App.tsx at /runtime-parameters/:userId.)
